import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Toast, ToastContainer } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiEdit,
  FiArrowRight,
  FiClipboard,
  FiPlay,
} from 'react-icons/fi';
import { TbFlask } from 'react-icons/tb';
import { interpretationsApi, testDataApi } from '../services/api';
import { Interpretation, PendingPatient } from '../types';
import { LoadingSpinner, StatusBadge, SLAIndicator } from '../components/common';
import { useVisiblePolling } from '../hooks';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface DashboardStats {
  pendingInterpretations: number;
  pendingTestData: number;
  approvedToday: number;
  editedToday: number;
}

// Two cycles, split by criticality. The operational one is what an auditor acts on;
// the counters are a running total of the day that nobody reads to the second, and
// they are by far the most expensive call on the screen (two hundred full records
// downloaded just to count them in the browser).
const OPERATIONAL_POLL_MS = 30000;
const COUNTERS_POLL_MS = 300000;

// Only ever fills fields that have never held a value: a cycle that fails does not
// call setStats at all, so it can never propagate these zeros over the other cycle's
// figures.
const EMPTY_STATS: DashboardStats = {
  pendingInterpretations: 0,
  pendingTestData: 0,
  approvedToday: 0,
  editedToday: 0,
};

const countReviewedToday = (items: Interpretation[]): number => {
  const today = new Date().toDateString();
  return items.filter(
    (i) => i.reviewed_at && new Date(i.reviewed_at).toDateString() === today
  ).length;
};


// Component to display test types with icons
const TestTypeBadges: React.FC<{ testTypes: string[] }> = ({ testTypes }) => {
  const hasLab = testTypes.some((t) => t === 'LAB_RESULTS');
  const hasQuestionnaire = testTypes.some((t) => t !== 'LAB_RESULTS');

  return (
    <div className="d-flex align-items-center gap-2">
      {hasLab && (
        <span className="test-icon lab" title="Estudios de laboratorio">
          <TbFlask size={16} />
        </span>
      )}
      {hasQuestionnaire && (
        <span className="test-icon quest" title="Cuestionarios">
          <FiClipboard size={14} />
        </span>
      )}
    </div>
  );
};

interface ToastMessage {
  show: boolean;
  message: string;
  variant: 'success' | 'danger' | 'warning' | 'info';
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentInterpretations, setRecentInterpretations] = useState<Interpretation[]>([]);
  const [urgentPatients, setUrgentPatients] = useState<PendingPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage>({ show: false, message: '', variant: 'info' });

  const showToast = (message: string, variant: ToastMessage['variant'] = 'info') => {
    setToast({ show: true, message, variant });
  };

  const handleGenerateInterpretation = async (patient: PendingPatient) => {
    setGeneratingFor(patient.patient_key);
    try {
      const testDataIds = patient.tests.map((t) => t.test_data_id);
      await interpretationsApi.generate({
        patient_key: patient.patient_key,
        test_data_ids: testDataIds,
      });
      // Redirect to interpretations list or show success
      navigate('/interpretations');
    } catch (error) {
      console.error('Error generating interpretation:', error);
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        showToast('Ya existe una interpretación para este paciente', 'warning');
      } else {
        showToast('Error al generar interpretación', 'danger');
      }
    } finally {
      setGeneratingFor(null);
    }
  };

  // Each cycle owns its try/catch and writes only its own fields. A cycle that fails
  // writes NOTHING -- in particular it does not write zeros over what the other cycle
  // put there. (Before the split both halves shared one catch, so a failure of the
  // counter calls also wiped the recent list.)
  const fetchOperational = async () => {
    try {
      const [pendingResponse, testData] = await Promise.all([
        interpretationsApi.getPending({ limit: 5 }),
        testDataApi.getPending(),
      ]);

      setRecentInterpretations(pendingResponse.items);

      setStats((prev) => ({
        ...(prev ?? EMPTY_STATS),
        pendingInterpretations: pendingResponse.total,
        pendingTestData: testData.total_tests,
      }));

      // Get urgent patients (SLA exceeded or close to deadline)
      // Show patients with <= 24h remaining OR SLA exceeded
      // Also show ALL pending if no urgent ones (for testing/demo)
      const urgentFiltered = testData.patients
        .filter((p) => p.sla_exceeded || p.hours_remaining <= 24);

      const urgent = urgentFiltered.length > 0
        ? urgentFiltered
        : testData.patients; // Show all pending if none are urgent

      setUrgentPatients(
        urgent
          .sort((a, b) => a.hours_remaining - b.hours_remaining)
          .slice(0, 5)
      );
    } catch (error) {
      console.error('Error fetching dashboard operational data:', error);
    }
  };

  const fetchApprovalCounters = async () => {
    try {
      const [approvedResponse, editedResponse] = await Promise.all([
        interpretationsApi.getAll({ status: 'approved', limit: 100 }),
        interpretationsApi.getAll({ status: 'approved_with_edit', limit: 100 }),
      ]);

      setStats((prev) => ({
        ...(prev ?? EMPTY_STATS),
        approvedToday: countReviewedToday(approvedResponse.items),
        editedToday: countReviewedToday(editedResponse.items),
      }));
    } catch (error) {
      console.error('Error fetching dashboard approval counters:', error);
    }
  };

  // Initial load: both cycles, and the spinner only comes down once both resolve --
  // the same first render as before the split.
  useEffect(() => {
    const loadInitial = async () => {
      await Promise.all([fetchOperational(), fetchApprovalCounters()]);
      setLoading(false);
    };

    loadInitial();
  }, []);

  // Polling, suspended while nobody is looking. Coming back refreshes both cycles, so
  // the counters are never five minutes stale for the auditor who just returned.
  useVisiblePolling(fetchOperational, OPERATIONAL_POLL_MS);
  useVisiblePolling(fetchApprovalCounters, COUNTERS_POLL_MS);

  if (loading) {
    return <LoadingSpinner message="Cargando dashboard..." />;
  }

  return (
    <div className="dashboard">
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1060 }}>
        <Toast
          show={toast.show}
          onClose={() => setToast({ ...toast, show: false })}
          delay={4000}
          autohide
          bg={toast.variant}
        >
          <Toast.Header closeButton>
            <strong className="me-auto">
              {toast.variant === 'danger' ? 'Error' :
               toast.variant === 'warning' ? 'Aviso' :
               toast.variant === 'success' ? 'Éxito' : 'Información'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toast.variant === 'danger' || toast.variant === 'warning' ? 'text-white' : ''}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="stat-label">Pendientes de Revisión</p>
                  <h2 className="stat-value">{stats?.pendingInterpretations || 0}</h2>
                </div>
                <div className="stat-icon bg-warning-subtle">
                  <FiFileText className="text-warning" />
                </div>
              </div>
              <Link to="/interpretations" className="stat-link">
                Ver todas <FiArrowRight />
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="stat-label">Datos Pendientes</p>
                  <h2 className="stat-value">{stats?.pendingTestData || 0}</h2>
                </div>
                <div className="stat-icon bg-info-subtle">
                  <FiClock className="text-info" />
                </div>
              </div>
              <Link to="/test-data" className="stat-link">
                Ver datos <FiArrowRight />
              </Link>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="stat-label">Aprobados Hoy</p>
                  <h2 className="stat-value text-success">{stats?.approvedToday || 0}</h2>
                </div>
                <div className="stat-icon bg-success-subtle">
                  <FiCheckCircle className="text-success" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="stat-label">Editadas Hoy</p>
                  <h2 className="stat-value" style={{ color: '#7C3AED' }}>{stats?.editedToday || 0}</h2>
                </div>
                <div className="stat-icon bg-purple-subtle">
                  <FiEdit style={{ color: '#7C3AED' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Recent Interpretations */}
        <Col lg={7}>
          <Card>
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Interpretaciones Recientes</h5>
                <Link to="/interpretations" className="btn btn-sm btn-outline-primary">
                  Ver todas
                </Link>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {recentInterpretations.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No hay interpretaciones pendientes
                </div>
              ) : (
                <div className="dashboard-list">
                  <div className="list-header">
                    <span>Paciente</span>
                    <span>Tipo</span>
                    <span>Estado</span>
                    <span>Fecha</span>
                  </div>
                  <div className="list-body">
                    {recentInterpretations.map((interpretation) => (
                      <div
                        key={interpretation.interpretation_id}
                        className="list-row"
                        onClick={() => navigate(`/interpretations/${interpretation.interpretation_id}`)}
                      >
                        <span className="col-patient">
                          <span className="patient-name">
                            {interpretation.raw_data?.patient_info?.name || interpretation.patient_id}
                          </span>
                        </span>
                        <span className="col-tests">
                          <TestTypeBadges testTypes={interpretation.test_types} />
                        </span>
                        <span className="col-status">
                          <StatusBadge status={interpretation.status || 'pending_review'} />
                        </span>
                        <span className="col-date">
                          {format(new Date(interpretation.created_at), 'dd MMM, HH:mm', {
                            locale: es,
                          })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Urgent Patients */}
        <Col lg={5}>
          <Card>
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Pacientes Urgentes</h5>
                <Link to="/test-data" className="btn btn-sm btn-outline-primary">
                  Ver todos
                </Link>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {urgentPatients.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No hay pacientes urgentes
                </div>
              ) : (
                <div className="urgent-patients-list">
                  {urgentPatients.map((patient) => (
                    <div
                      key={patient.patient_key}
                      className={`urgent-patient-item ${patient.sla_exceeded ? 'urgent' : ''}`}
                    >
                      <div className="patient-info">
                        <div className="patient-name">{patient.patient_name}</div>
                        <div className="patient-tests">{patient.test_count} {patient.test_count === 1 ? 'examen' : 'exámenes'}</div>
                      </div>
                      <SLAIndicator
                        hoursRemaining={patient.hours_remaining}
                        slaExceeded={patient.sla_exceeded}
                      />
                      <button
                        className="interpret-action-btn"
                        onClick={() => handleGenerateInterpretation(patient)}
                        disabled={generatingFor === patient.patient_key}
                      >
                        {generatingFor === patient.patient_key ? (
                          <span className="loading-dots">...</span>
                        ) : (
                          <FiPlay size={12} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
